// frontend/src/api/valuation.ts
//
// VALUATOR-UI-1 — typed API client for the Business Valuator surface.
//
// Mirrors the style of api/tax.ts: thin wrappers around `apiClient`
// (baseURL = VITE_API_BASE_URL + '/api'), so every path below is relative
// to `/api` and resolves to the routes in apps/api/routers/valuation.py.
//
// There is no valuation math in this file, and there must never be: the
// signed methodology lives in core/valuation and the band comes back off
// POST .../run. The client's job is to render what the engine disclosed.

import { apiClient } from '@/api/client'
import type {
  CreateEngagementBody,
  UpdateEngagementBody,
  ValuationEngagement,
  ValuationMethodology,
  ValuationProfilesResponse,
} from '@/types/valuation'
import type { SnapshotHistoryResponse } from '@/types/valuationB3'

/** Every signed industry profile, with governance + signed bands. */
export async function fetchValuationProfiles(): Promise<ValuationProfilesResponse> {
  const { data } = await apiClient.get<ValuationProfilesResponse>('/v1/valuation/profiles')
  return data
}

/** The signed methodology the intake form renders against. */
export async function fetchValuationMethodology(): Promise<ValuationMethodology> {
  const { data } = await apiClient.get<ValuationMethodology>('/v1/valuation/methodology')
  return data
}

/** Firm-scoped list of valuation engagements. */
/** `openFirst` (VAL-DASHBOARD-NEXT-1): open engagements first, oldest last-edited first, then signed. */
export async function listValuationEngagements(
  opts: { openFirst?: boolean } = {},
): Promise<ValuationEngagement[]> {
  const { data } = await apiClient.get<ValuationEngagement[]>('/v1/valuation/engagements', {
    params: opts.openFirst ? { open_first: 1 } : undefined,
  })
  return data
}

export async function getValuationEngagement(id: string): Promise<ValuationEngagement> {
  const { data } = await apiClient.get<ValuationEngagement>(`/v1/valuation/engagements/${id}`)
  return data
}

/**
 * Open an engagement. `value_kind` is fixed here for the life of the
 * engagement — the API deliberately has no PATCH path that promotes a
 * calculation engagement into a conclusion of value.
 */
export async function createValuationEngagement(
  body: CreateEngagementBody,
): Promise<ValuationEngagement> {
  const { data } = await apiClient.post<ValuationEngagement>('/v1/valuation/engagements', body)
  return data
}

export async function updateValuationEngagement(
  id: string,
  body: UpdateEngagementBody,
): Promise<ValuationEngagement> {
  const { data } = await apiClient.patch<ValuationEngagement>(
    `/v1/valuation/engagements/${id}`,
    body,
  )
  return data
}

/** VAL-OVERVIEW-1 §3 R6 — every signed version of this engagement, newest first. */
export async function fetchValuationSnapshots(id: string): Promise<SnapshotHistoryResponse> {
  const { data } = await apiClient.get<SnapshotHistoryResponse>(
    `/v1/valuation/engagements/${id}/snapshots`,
  )
  return data
}

/**
 * VAL-OVERVIEW-1C §DATA R7 — the issued-document archive: valuation reports
 * the platform produced, keyed by snapshot_version, superseded versions
 * included on purpose. These are NOT uploaded source documents — valuation
 * has no upload route at all.
 */
export interface ValuationDocumentRow {
  document_id: string
  snapshot_version: number
  sha256: string
  byte_size: number
  media_type: string
  filename: string
  blob_status: string
  retrievable: boolean
  produced_by: string
  produced_by_role: string
  produced_at: string
  /** Display name for produced_by, resolved at read time (VAL-DOCS-CARD-1). */
  produced_by_name?: string
}

export interface ValuationDocumentsResponse {
  valuation_engagement_id: string
  documents: ValuationDocumentRow[]
}

export async function fetchValuationDocuments(id: string): Promise<ValuationDocumentsResponse> {
  const { data } = await apiClient.get<ValuationDocumentsResponse>(
    `/v1/valuation/engagements/${id}/documents`,
  )
  return data
}
